
// Augment window interface to include our config
declare global {
    interface Window {
        APP_CONFIG?: {
            BACKEND_URL: string;
            RECAPTCHA_SITE_KEY?: string;
        };
    }
}

// =====================================================================================
// RUNTIME CONFIGURATION
// The URL is now loaded from public/config.js (window.APP_CONFIG)
// This allows you to change the environment without rebuilding the code.
// =====================================================================================

const getConfiguredBackendUrl = (): string => {
    // Default fallback
    let url = typeof window !== 'undefined' ? window.location.origin : "https://api.bytecohortreach.com";

    // Check for runtime config (Production/Cloud Run usage)
    if (typeof window !== 'undefined' && window.APP_CONFIG && window.APP_CONFIG.BACKEND_URL) {
        url = window.APP_CONFIG.BACKEND_URL;
    }
    
    // Ensure no trailing slash
    return url.endsWith('/') ? url.slice(0, -1) : url;
};

const BACKEND_URL = getConfiguredBackendUrl();

console.log(`[API] Initialized with Backend URL: ${BACKEND_URL}`);

export const getBackendUrl = (): string => {
    return BACKEND_URL;
};

const getApiBaseUrl = (): string => {
    return `${BACKEND_URL}/api`;
};

const getAuthToken = (): string | null => {
    return localStorage.getItem('whatsapp_sender_token');
};

const handleResponse = async (response: Response) => {
    // Handle 401 Unauthorized specifically (Expired or invalid token)
    if (response.status === 401) {
        console.warn("[API] Unauthorized request (401). Clearing session.");
        localStorage.removeItem('whatsapp_sender_token');
        // If we are not already on the landing page, redirect to login
        if (!window.location.hash.includes('/login') && window.location.hash !== '' && window.location.hash !== '#/') {
            window.location.hash = '/app';
        }
    }

    // For streaming responses, we handle the body elsewhere
    if (response.headers.get('Content-Type')?.includes('text/plain')) {
        return response;
    }

    const text = await response.text();
    let data;
    try {
        data = JSON.parse(text);
    } catch (e) {
        // If response is not JSON, it could be an HTML error page from a proxy or server crash
        // This is likely what is happening with the 425 byte response in logs
        throw new Error(`Server returned a non-JSON response (Status: ${response.status}). This often indicates a server-side crash or proxy error.`);
    }

    if (!response.ok) {
        const error = new Error(data.message || 'An error occurred with the API request.');
        // Attach the error code from the backend response, if it exists
        (error as any).code = data.code; 
        (error as any).status = response.status;
        (error as any).response = {
            status: response.status,
            data: data
        };
        throw error;
    }
    return data;
};

const makeRequest = async (method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH', endpoint: string, body?: object): Promise<any> => {
    const baseUrl = getApiBaseUrl();
    const token = getAuthToken();
    
    const headers: HeadersInit = {
        // Only attach Bearer if token actually exists to avoid sending "Bearer null"
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };
    
    const config: RequestInit = {
        method,
        headers,
    };

    if (body) {
        (headers as any)['Content-Type'] = 'application/json';
        config.body = JSON.stringify(body);
    }

    try {
        const response = await fetch(`${baseUrl}${endpoint}`, config);
        return await handleResponse(response);
    } catch (error: any) {
        // Log the specific network error (e.g. Failed to fetch)
        console.error(`[API Error] ${method} ${endpoint}:`, error.message);
        throw error;
    }
};

const makeStreamRequest = async (endpoint: string, body: object, signal: AbortSignal): Promise<Response> => {
    const baseUrl = getApiBaseUrl();
    const token = getAuthToken();
    
    const response = await fetch(`${baseUrl}${endpoint}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
        signal,
    });

    if (!response.ok || !response.body) {
         const errorData = await response.json().catch(() => ({ message: `Server error: ${response.status}` }));
         const error = new Error(errorData.message);
         (error as any).code = errorData.code;
         (error as any).status = response.status;
         (error as any).response = {
             status: response.status,
             data: errorData
         };
         throw error;
    }
    
    return response;
};

export const api = {
    get: (endpoint: string) => makeRequest('GET', endpoint),
    post: (endpoint: string, body: object) => makeRequest('POST', endpoint, body),
    put: (endpoint: string, body: object) => makeRequest('PUT', endpoint, body),
    patch: (endpoint: string, body: object) => makeRequest('PATCH', endpoint, body),
    delete: (endpoint: string) => makeRequest('DELETE', endpoint),
    postStream: (endpoint: string, body: object, signal: AbortSignal) => makeStreamRequest(endpoint, body, signal),
};
